import React from 'react';
import LegalProse from '../components/legal/LegalProse';

export function PrivacyContent() {
  return (
    <LegalProse>
      <p>
        Country Chicken Co. (&ldquo;CCC&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) operates the website{' '}
        <a href="https://countrychickenco.in/">https://countrychickenco.in/</a> (the &ldquo;Site&rdquo;). We are a Data
        Fiduciary under the Digital Personal Data Protection Act, 2023 (&ldquo;DPDP Act&rdquo;) and the Digital Personal
        Data Protection Rules, 2025 (&ldquo;DPDP Rules&rdquo;), and we are committed to protecting the personal data of
        every Data Principal (the individual to whom the personal data relates) who interacts with us.
      </p>
      <p>
        This Privacy Policy explains what personal data we collect, why we collect it, how we use, store, share, and
        protect it, and the rights you have over your personal data. Please read it carefully before using the Site or
        providing us with any personal data.
      </p>
      <p>
        By using the Site or sharing your personal data with us, you confirm that you have read and understood this
        Privacy Policy.
      </p>

      <h4>1. Who We Are</h4>
      <ul>
        <li>
          <strong>Data Fiduciary:</strong> Country Chicken Co., a brand of premium natural and heritage breed meat
          products, headquartered in Hyderabad, Telangana, India.
        </li>
        <li>
          <strong>Website:</strong> <a href="https://countrychickenco.in/">https://countrychickenco.in/</a>
        </li>
        <li>
          <strong>Grievance Officer Email:</strong> cto@countrychickenco.in
        </li>
      </ul>

      <h4>2. Scope and Applicability</h4>
      <p>This Privacy Policy applies to digital personal data we collect from or about you when you:</p>
      <ul>
        <li>Visit, browse, or interact with our Site;</li>
        <li>
          Place an order or make a purchase from us (online or in-store, where data is later digitised);
        </li>
        <li>Sign up for our newsletter, WhatsApp updates, or marketing communications;</li>
        <li>Contact us through any form, email, phone, or chat channel; or</li>
        <li>Engage with our advertising or campaigns on third-party platforms.</li>
      </ul>
      <p>
        In line with the DPDP Act, this policy covers personal data in digital form, including data collected offline
        and subsequently digitised. It does not apply to personal data that you have voluntarily made publicly available.
      </p>

      <h4>3. Personal Data We Collect</h4>
      <p>
        We collect only the personal data that is reasonably necessary for the purposes set out in this policy. The
        categories of personal data we may collect include:
      </p>

      <h4>3.1 Information You Provide to Us</h4>
      <ul>
        <li>
          <strong>Identity and Contact Information:</strong> name, mobile number, email address, delivery address, and
          city of residence.
        </li>
        <li>
          <strong>Order and Transaction Information:</strong> items purchased, order history, delivery instructions, and
          order timing. Payment is processed by our authorised payment gateways; we do not store your full card number,
          CVV, UPI PIN, or net-banking credentials on our servers.
        </li>
        <li>
          <strong>Account Information:</strong> where you create an account, your login credentials and any preferences
          you set.
        </li>
        <li>
          <strong>Communications:</strong> messages, queries, feedback, reviews, or complaints you send us through forms,
          email, WhatsApp, or other channels.
        </li>
        <li>
          <strong>Marketing Preferences:</strong> your consent choices for newsletters, WhatsApp campaigns, SMS, and
          promotional communications.
        </li>
      </ul>

      <h4>3.2 Information Collected Automatically</h4>
      <p>
        When you visit the Site, certain information is automatically collected from your device. We refer to this as
        &ldquo;Device Information&rdquo; and it may include:
      </p>
      <ul>
        <li>IP address and approximate location;</li>
        <li>Browser type and version, operating system, and device type;</li>
        <li>Time zone and language settings;</li>
        <li>Pages visited, products viewed, time spent, click paths, and referring URLs; and</li>
        <li>Cookie identifiers and similar tracking information (see Section 8 on Cookies).</li>
      </ul>

      <h4>3.3 Information from Third Parties</h4>
      <p>
        We may receive limited personal data from advertising and analytics partners (such as Meta and Google) when you
        interact with our advertisements, and from delivery and logistics partners who fulfil your orders.
      </p>

      <h4>4. Purposes for Which We Process Your Personal Data</h4>
      <p>
        We process your personal data, in an itemised manner as required under Rule 3 of the DPDP Rules, 2025, for the
        following specific purposes:
      </p>
      <ul>
        <li>
          <strong>Order fulfilment:</strong> to accept, process, pack, deliver, and invoice your orders.
        </li>
        <li>
          <strong>Customer support:</strong> to respond to your queries, complaints, refund requests, and feedback.
        </li>
        <li>
          <strong>Account management:</strong> to create and maintain your account and authenticate logins.
        </li>
        <li>
          <strong>Marketing and communications:</strong> to send you newsletters, WhatsApp campaigns, offers, and
          updates about CCC products, where you have given consent.
        </li>
        <li>
          <strong>Site improvement and analytics:</strong> to understand how the Site is used, identify abuse or fraud,
          and improve user experience.
        </li>
        <li>
          <strong>Personalisation:</strong> to recommend products and tailor content based on your preferences and order
          history.
        </li>
        <li>
          <strong>Legal and regulatory compliance:</strong> to comply with applicable Indian laws, including tax,
          consumer protection, food safety, and DPDP requirements.
        </li>
        <li>
          <strong>Security:</strong> to protect the Site, our customers, and our business from unauthorised access,
          fraud, or misuse.
        </li>
      </ul>

      <h4>5. Lawful Basis for Processing</h4>
      <p>Under the DPDP Act, we process your personal data on the following bases:</p>
      <ul>
        <li>
          <strong>Your Consent:</strong> where you have given us free, specific, informed, unconditional, and unambiguous
          consent through a clear affirmative action (for example, ticking a marketing opt-in box, providing your phone
          number to receive WhatsApp updates, or accepting cookies).
        </li>
        <li>
          <strong>Certain Legitimate Uses (Section 7 of the DPDP Act):</strong> where the law permits processing without
          separate consent, such as for fulfilling an order you have voluntarily placed, responding to a medical
          emergency, or complying with a legal obligation.
        </li>
      </ul>
      <p>
        Your consent is requested in clear and plain language, and you can withdraw it at any time as easily as you gave
        it (see Section 7).
      </p>

      <h4>6. How We Share Your Personal Data</h4>
      <p>
        We do not sell your personal data. We share it only with the following categories of recipients, and only to the
        extent necessary:
      </p>
      <ul>
        <li>
          <strong>Service providers (Data Processors):</strong> logistics and delivery partners, payment gateways, cloud
          hosting providers, IT and customer support tools, marketing automation platforms, WhatsApp Business solution
          providers, and analytics services. These partners are contractually bound to process your data only on our
          instructions and to maintain reasonable security safeguards.
        </li>
        <li>
          <strong>Advertising partners:</strong> Meta (Facebook/Instagram), Google, and similar platforms, where you
          have consented to such processing, for the purpose of showing you relevant ads and measuring ad performance.
        </li>
        <li>
          <strong>Authorities and law enforcement:</strong> where required or permitted by Indian law, including in
          response to a lawful order, subpoena, or government request, or to protect our legal rights, prevent fraud, or
          safeguard the safety of others.
        </li>
        <li>
          <strong>In the event of a business transfer:</strong> if CCC is involved in a merger, acquisition,
          restructuring, or sale of assets, your personal data may be transferred as part of that transaction, subject
          to applicable law.
        </li>
      </ul>

      <h4>7. Your Rights as a Data Principal</h4>
      <p>
        Under the DPDP Act, you have the following rights in relation to your personal data. You may exercise any of
        these rights by writing to our Grievance Officer at the contact details in Section 15.
      </p>
      <ul>
        <li>
          <strong>Right to Access (Section 11):</strong> to obtain a summary of the personal data we hold about you and
          the processing activities we have undertaken.
        </li>
        <li>
          <strong>Right to Correction and Erasure (Section 12):</strong> to request that we correct inaccurate or
          incomplete data, update outdated data, or erase personal data that is no longer needed for the purpose for
          which it was collected (subject to legal retention requirements).
        </li>
        <li>
          <strong>Right to Withdraw Consent:</strong> to withdraw your consent at any time. Withdrawal will not affect
          the lawfulness of processing carried out before the withdrawal. Once consent is withdrawn, we will stop the
          relevant processing unless another lawful basis applies (for example, completing an order you have already
          placed).
        </li>
        <li>
          <strong>Right of Grievance Redressal (Section 13):</strong> to raise a grievance with our Grievance Officer
          regarding any aspect of how we handle your personal data.
        </li>
        <li>
          <strong>Right to Nominate (Section 14):</strong> to nominate another person who may exercise these rights on
          your behalf in the event of your death or incapacity.
        </li>
      </ul>
      <p>
        <strong>How to exercise your rights:</strong> Send a written request to cto@countrychickenco.in stating clearly
        which right you wish to exercise and the personal data concerned. We may need to verify your identity before
        acting on the request. We will respond to your grievance within 90 days of receipt, in line with Rule 14 of the
        DPDP Rules, 2025.
      </p>
      <p>
        If you are not satisfied with our response, you may file a complaint with the Data Protection Board of India
        through its digital platform once it is operational.
      </p>

      <h4>8. Cookies and Similar Technologies</h4>
      <p>
        Our Site uses cookies and similar technologies to make the Site work, remember your preferences, understand how
        the Site is used, and (with your consent) deliver relevant advertising.
      </p>
      <p>
        <strong>Categories of cookies we use:</strong>
      </p>
      <ul>
        <li>
          <strong>Strictly necessary cookies:</strong> required for the Site to function (for example, to maintain your
          shopping cart and login session). These are set on the basis of legitimate use under Section 7 of the DPDP
          Act.
        </li>
        <li>
          <strong>Analytics cookies:</strong> help us understand how visitors use the Site (for example, Google
          Analytics). Set only with your consent.
        </li>
        <li>
          <strong>Marketing and advertising cookies:</strong> used by partners such as Meta Pixel and Google Ads to
          measure advertising performance and show you relevant ads. Set only with your consent.
        </li>
      </ul>
      <p>
        You can manage your cookie preferences through the cookie consent banner shown when you first visit the Site,
        and you can withdraw or change your choices at any time through your browser settings or by contacting us.
      </p>

      <h4>9. Data Retention</h4>
      <p>
        We retain your personal data only for as long as is necessary to fulfil the purposes for which it was collected,
        including any legal, accounting, tax, or reporting requirements. Indicative retention periods are:
      </p>
      <ul>
        <li>
          <strong>Order and transaction records:</strong> retained for the period required under Indian tax, GST, and
          FSSAI regulations (typically up to 8 years from the end of the relevant financial year).
        </li>
        <li>
          <strong>Customer account data:</strong> retained while your account is active. If you have not engaged with us
          for an extended period, we may erase your account data after giving you reasonable advance notice, in line with
          Rule 8 of the DPDP Rules, 2025.
        </li>
        <li>
          <strong>Marketing data:</strong> retained until you withdraw consent or unsubscribe, after which it is deleted
          or anonymised within a reasonable period.
        </li>
        <li>
          <strong>Cookie data:</strong> retained for the duration set out in our cookie consent tool.
        </li>
      </ul>
      <p>
        Once retention periods expire and the purpose of processing is fulfilled, we will erase, anonymise, or securely
        destroy the data.
      </p>

      <h4>10. Security Safeguards</h4>
      <p>
        We implement reasonable technical and organisational security safeguards to protect your personal data against
        unauthorised access, use, disclosure, alteration, or loss. These include:
      </p>
      <ul>
        <li>Encryption of data in transit using HTTPS/TLS;</li>
        <li>Access controls and role-based permissions for staff and authorised processors;</li>
        <li>Regular monitoring of systems, logs, and access patterns;</li>
        <li>Contractual data protection obligations with our service providers; and</li>
        <li>Periodic review of our information security practices.</li>
      </ul>
      <p>
        While we take reasonable steps to safeguard your data, no method of transmission over the internet or electronic
        storage is fully secure, and we cannot guarantee absolute security.
      </p>

      <h4>11. Personal Data Breach Notification</h4>
      <p>
        In the unlikely event of a personal data breach affecting your information, we will notify you and the Data
        Protection Board of India without undue delay, in line with Section 8(6) of the DPDP Act and Rule 7 of the DPDP
        Rules, 2025. Our notification will include, where applicable, a description of the breach, the data affected,
        the likely consequences, the measures we are taking, and steps you can take to protect yourself.
      </p>

      <h4>12. Children&apos;s Personal Data</h4>
      <p>
        Our Site and products are intended for adults. We do not knowingly process personal data of children (individuals
        under 18 years of age) without verifiable consent from a parent or lawful guardian, in line with Section 9 of the
        DPDP Act and Rule 10 of the DPDP Rules, 2025.
      </p>
      <p>
        We do not undertake tracking, behavioural monitoring, or targeted advertising directed at children. If you believe
        a child has provided us with personal data without proper parental consent, please contact our Grievance Officer
        and we will take appropriate steps to delete the information.
      </p>

      <h4>13. Cross-Border Transfers</h4>
      <p>
        Some of our service providers (for example, cloud hosting and analytics providers) may process your personal data
        outside India. Where this happens, the transfer is made in accordance with the conditions and restrictions notified
        by the Central Government under Section 16 of the DPDP Act, and we ensure appropriate contractual safeguards are
        in place to protect your data.
      </p>

      <h4>14. Third-Party Links</h4>
      <p>
        Our Site may contain links to third-party websites, applications, or services that we do not own or control (for
        example, social media platforms, payment gateways, or partner sites). This Privacy Policy does not apply to those
        third parties. We encourage you to review the privacy policies of any third-party site you visit, as we are not
        responsible for their practices.
      </p>

      <h4>15. Grievance Officer and Contact</h4>
      <p>
        If you have any questions, concerns, or grievances about this Privacy Policy or how we handle your personal data,
        or if you wish to exercise any of your rights, please contact our Grievance Officer:
      </p>
      <p>
        <strong>Grievance Officer</strong>
        <br />
        Country Chicken Co.
        <br />
        Email: cto@countrychickenco.in
        <br />
        Website: <a href="https://countrychickenco.in/">https://countrychickenco.in/</a>
      </p>
      <p>
        We will acknowledge your communication promptly and resolve grievances within 90 days of receipt.
      </p>
      <p>
        If your grievance remains unresolved, you may approach the Data Protection Board of India in accordance with the
        DPDP Act.
      </p>

      <h4>16. Updates to This Privacy Policy</h4>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal
        requirements, or the DPDP framework. The updated policy will be posted on this page with a revised &ldquo;Last
        Updated&rdquo; date. Material changes will be communicated to you through the Site or, where appropriate, via
        email or other direct channels.
      </p>

      <h4>17. Governing Law and Jurisdiction</h4>
      <p>
        This Privacy Policy is governed by the laws of India, including the Digital Personal Data Protection Act, 2023,
        the Digital Personal Data Protection Rules, 2025, and the Information Technology Act, 2000, to the extent
        applicable. Any disputes arising out of or in connection with this Privacy Policy shall be subject to the
        exclusive jurisdiction of the courts at Hyderabad, Telangana.
      </p>

      <p>
        <strong>Country Chicken Co.</strong>
        <br />
        Real food. Real farmers. Real care for your data.
      </p>
    </LegalProse>
  );
}
